<template>
  <div class="review-root">
    <div class="hs-summary">
      <div class="hs-stats">
        <div v-for="card in cards" :key="card.name" class="hs-stat">
          <span class="hs-stat-dot" :class="card.tone"></span>
          <span class="hs-stat-value">{{ card.value }}</span>
          <span class="hs-stat-name">{{ card.name }}</span>
        </div>
      </div>
      <div class="hs-total">
        <span class="hs-total-label">{{ totalLabel }}</span>
        <span class="hs-total-values">
          <span class="hs-total-old">{{ figure(currentTotal) }}</span>
          <i class="fas fa-arrow-right hs-total-arrow"></i>
          <strong class="hs-total-new">{{ figure(newTotal) }}</strong>
          <span class="hs-diff" :class="differenceClass">{{ differenceText }}</span>
        </span>
      </div>
    </div>

    <div v-if="review.summary.error > 0" class="hs-alert hs-alert-danger">
      <i class="fas fa-exclamation-circle"></i>
      <span>{{ review.summary.error }} row(s) have a problem. A wrong {{ correctableNames }} can be corrected here. For any other problem, fix the file and upload it again. Nothing is saved while a row has a problem.</span>
    </div>
    <div v-for="warning in review.warnings" :key="warning" class="hs-alert hs-alert-warning">
      <i class="fas fa-exclamation-triangle"></i>
      <span>{{ warning }}</span>
    </div>

    <div class="hs-toolbar">
      <div class="hs-segment">
        <button
            v-for="item in filters"
            :key="item.id"
            type="button"
            class="hs-segment-item"
            :class="{ 'hs-segment-active': filter === item.id }"
            @click="filter = item.id"
        >
          {{ item.name }}<span class="hs-segment-count">{{ item.count }}</span>
        </button>
      </div>
      <span class="hs-edit-chip"><i class="fas fa-pen"></i>Type {{ typedNames('and') }}; the rest fills itself, as in the Excel.<template v-if="splitNames.length > 0"> You can also change any {{ listText(splitNames, 'or') }}.</template></span>
      <div class="hs-search">
        <i class="fas fa-search hs-search-icon"></i>
        <input v-model="search" type="text" class="form-control form-control-sm" placeholder="Search head, code or group">
      </div>
    </div>

    <div class="table-wrap">
      <table class="table table-sm review-table mb-0">
        <thead>
          <tr class="head-row-one">
            <th rowspan="2" class="col-head">Budget Head</th>
            <th rowspan="2" class="text-end">Qty<i class="fas fa-pen edit-mark"></i></th>
            <th v-if="hasAmount" rowspan="2" class="text-end">Rate<span v-if="isTyped('rate')"><i class="fas fa-pen edit-mark"></i></span></th>
            <th v-if="hasAmount" rowspan="2" class="text-end">Amount<span v-if="isTyped('amount')"><i class="fas fa-pen edit-mark"></i></span></th>
            <th v-for="(period, index) in review.periods" :key="period" :colspan="hasAmount ? 2 : 1" class="text-center period-name period-start" :class="bandClass(index)">{{ period }}</th>
            <th v-for="(member, index) in review.members" :key="'member-' + index" class="text-center period-name member-name period-start" :title="member">{{ member }}</th>
          </tr>
          <tr class="head-row-two">
            <template v-for="(period, index) in review.periods" :key="period + '-columns'">
              <th class="text-end period-start" :class="bandClass(index)">Qty</th>
              <th v-if="hasAmount" class="text-end" :class="bandClass(index)">Amount</th>
            </template>
            <th v-for="(member, index) in review.members" :key="'member-qty-' + index" class="text-end period-start">Qty</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in pageRows" :key="row.row_no" :class="['row-' + row.status, { 'row-busy': isBusy(row) }]">
            <td class="col-head">
              <div class="hs-head-line">
                <span class="hs-head-name">{{ row.name }}</span>
                <span class="hs-head-tags">
                  <span v-if="isBusy(row)" class="spinner-border spinner-border-sm text-primary" role="status"></span>
                  <span v-if="isEdited(row)" class="hs-tag hs-tag-edited">Edited</span>
                  <span v-if="row.status !== 'unchanged'" class="hs-tag" :class="'hs-tag-' + row.status">
                    <span class="hs-tag-dot"></span>{{ statusName[row.status] }}
                  </span>
                </span>
              </div>
              <p class="hs-head-meta mb-0">{{ describe(row) }}</p>
              <p v-for="error in row.errors" :key="error.column + error.message" class="hs-row-error mb-0">
                <i class="fas fa-times-circle me-25"></i>Row {{ row.row_no }} · {{ error.column }}: {{ error.message }}
              </p>
              <p v-for="note in row.notes" :key="note" class="hs-row-note mb-0">{{ note }}</p>
              <p v-if="hasAmount && row.amount_locked" class="hs-row-note mb-0">Qty only, as on the budget form. Rate and Amount stay as saved.</p>
              <button v-if="isEdited(row)" type="button" class="hs-undo" @click="emit('onUndoRow', row)">
                <i class="fas fa-undo me-25"></i>Undo my changes
              </button>
            </td>
            <BudgetHeadSheetCell
                :value="row.quantity"
                :changed="isTotalChanged(row, 'quantity')"
                :title="totalTitle(row, 'quantity')"
                :editable="canEdit(row, 'quantity')"
                :busy="isBusy(row)"
                @onCommit="edit(row, 'quantity', $event)"
            />
            <BudgetHeadSheetCell
                v-if="hasAmount"
                :value="row.rate"
                :changed="isTotalChanged(row, 'rate')"
                :title="totalTitle(row, 'rate')"
                :editable="canEdit(row, 'rate')"
                :busy="isBusy(row)"
                @onCommit="edit(row, 'rate', $event)"
            />
            <BudgetHeadSheetCell
                v-if="hasAmount"
                class="cell-amount"
                :class="{ 'cell-auto': ! isTyped('amount') && ! row.amount_locked }"
                :value="row.amount"
                :changed="isTotalChanged(row, 'amount')"
                :title="totalTitle(row, 'amount')"
                :editable="canEdit(row, 'amount')"
                :busy="isBusy(row)"
                @onCommit="edit(row, 'amount', $event)"
            >
              <span v-if="isTotalChanged(row, 'amount')" class="d-block was-value">{{ totalTitle(row, 'amount') }}</span>
              <span v-if="sourceName[row.amount_source]" class="d-block source-name">{{ sourceName[row.amount_source] }}</span>
            </BudgetHeadSheetCell>
            <template v-for="(period, index) in review.periods" :key="period + '-' + row.row_no">
              <BudgetHeadSheetCell
                  plain
                  class="period-start"
                  :class="bandClass(index)"
                  :value="row.period_qty[index]"
                  :changed="isPeriodChanged(row, 'qty', index)"
                  :title="periodTitle(row, 'qty', index)"
                  :editable="canEdit(row, 'period_qty')"
                  :busy="isBusy(row)"
                  @onCommit="editSplit(row, 'period_qty', index, $event)"
              />
              <BudgetHeadSheetCell
                  v-if="hasAmount"
                  plain
                  :class="bandClass(index)"
                  :value="row.period_amount[index]"
                  :changed="isPeriodChanged(row, 'amount', index)"
                  :title="periodTitle(row, 'amount', index)"
                  :editable="canEdit(row, 'period_amount')"
                  :busy="isBusy(row)"
                  @onCommit="editSplit(row, 'period_amount', index, $event)"
              />
            </template>
            <BudgetHeadSheetCell
                v-for="(member, index) in review.members"
                :key="'team-' + index + '-' + row.row_no"
                plain
                class="period-start"
                :value="row.team_qty[index]"
                :changed="isPeriodChanged(row, 'team', index)"
                :title="periodTitle(row, 'team', index)"
                :editable="canEdit(row, 'team_qty')"
                :busy="isBusy(row)"
                @onCommit="editSplit(row, 'team_qty', index, $event)"
            />
          </tr>
        </tbody>
      </table>
      <div v-if="pageRows.length < 1" class="hs-empty">
        <i class="fas fa-search"></i>
        <p class="mb-0">No rows match this filter.</p>
      </div>
    </div>

    <div class="hs-pager">
      <span class="hs-pager-range">{{ rangeText }}</span>
      <div class="hs-pager-controls">
        <button type="button" class="hs-page-btn" aria-label="Previous page" :disabled="page <= 1" @click="page = page - 1">
          <i class="fas fa-chevron-left"></i>
        </button>
        <span class="hs-pager-text">Page {{ page }} of {{ pageCount }}</span>
        <button type="button" class="hs-page-btn" aria-label="Next page" :disabled="page >= pageCount" @click="page = page + 1">
          <i class="fas fa-chevron-right"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import figureFormatter from '@/services/utils/figureFormatter'
import BudgetHeadSheetCell from './BudgetHeadSheetCell.vue'

const props = defineProps({
  review: { type: Object, required: true },
  busyHeads: { type: Array, default: () => [] },
  editedHeads: { type: Array, default: () => [] },
  canEditField: { type: Function, required: true }
})

const emit = defineEmits(['onEditCell', 'onUndoRow'])

const { commaFormat } = figureFormatter()

const pageSize = 50
const headColumn = 'Head ID'
const statusesOf = {
  changes: ['new', 'updated'],
  new: ['new'],
  updated: ['updated'],
  error: ['error'],
  all: ['new', 'updated', 'unchanged', 'error']
}
const statusName = { new: 'New', updated: 'Updated', unchanged: 'Unchanged', error: 'Error' }
const sourceName = { periods: 'period total', qty_rate: 'Qty x Rate', typed: 'typed' }
const periodField = { qty: 'period_qty', amount: 'period_amount', team: 'team_qty' }
const oldPeriodField = { qty: 'old_period_qty', amount: 'old_period_amount', team: 'old_team_qty' }
const columnName = { quantity: 'Qty', rate: 'Rate', amount: 'Amount' }

const filter = ref(props.review.summary.error > 0 ? 'error' : 'changes')
const search = ref('')
const page = ref(1)

const cards = computed(() => [
  { name: 'Heads in file', value: props.review.summary.total, tone: 'tone-plain' },
  { name: 'New', value: props.review.summary.new, tone: 'tone-success' },
  { name: 'Updated', value: props.review.summary.updated, tone: 'tone-primary' },
  { name: 'Unchanged', value: props.review.summary.unchanged, tone: 'tone-plain' },
  { name: 'Errors', value: props.review.summary.error, tone: 'tone-danger' },
  { name: 'Rows ignored', value: props.review.summary.ignored, tone: 'tone-warning', optional: true }
].filter(card => ! card.optional || card.value > 0))

const filters = computed(() => [
  { id: 'changes', name: 'Changes', count: props.review.summary.new + props.review.summary.updated },
  { id: 'new', name: 'New', count: props.review.summary.new },
  { id: 'updated', name: 'Updated', count: props.review.summary.updated },
  { id: 'error', name: 'Errors', count: props.review.summary.error },
  { id: 'all', name: 'All', count: props.review.summary.total }
])

const hasAmount = computed(() => props.review.layout.has_amount)

const totalField = computed(() => {
  if (hasAmount.value) return 'amount'

  return 'quantity'
})

const totalLabel = computed(() => {
  if (hasAmount.value) return 'Total of these heads'

  return 'Total Qty of these heads'
})

const currentTotal = computed(() => props.review.summary['current_' + totalField.value])

const newTotal = computed(() => props.review.summary['new_' + totalField.value])

const difference = computed(() => newTotal.value - currentTotal.value)

const differenceText = computed(() => {
  if (Math.abs(difference.value) < 0.005) return 'no change'
  if (difference.value > 0) return '+' + commaFormat(difference.value)

  return '-' + commaFormat(Math.abs(difference.value))
})

const differenceClass = computed(() => {
  if (Math.abs(difference.value) < 0.005) return 'hs-diff-none'
  if (difference.value > 0) return 'hs-diff-up'

  return 'hs-diff-down'
})

const filteredRows = computed(() => {
  const term = search.value.trim().toLowerCase()
  const statuses = statusesOf[filter.value]

  return props.review.rows.filter(row => statuses.includes(row.status) && matchesSearch(row, term))
})

const pageCount = computed(() => Math.max(1, Math.ceil(filteredRows.value.length / pageSize)))

const pageRows = computed(() => filteredRows.value.slice((page.value - 1) * pageSize, page.value * pageSize))

const rangeText = computed(() => {
  if (filteredRows.value.length < 1) return 'Showing 0 heads'

  const first = (page.value - 1) * pageSize + 1
  const last = Math.min(page.value * pageSize, filteredRows.value.length)

  return `Showing ${first} to ${last} of ${filteredRows.value.length} heads`
})

const matchesSearch = (row, term) => {
  if (! term) return true

  return [row.name, row.code, row.group].some(text => text && String(text).toLowerCase().includes(term))
}

const figure = (value) => {
  if (! value) return '0.00'

  return commaFormat(value)
}

const describe = (row) => [row.code, row.group, row.unit_name].filter(text => text).join(' · ')

const differs = (first, second) => Math.abs(first - second) >= 0.005

const isTotalChanged = (row, field) => row.status === 'updated' && differs(row[field], row['old_' + field])

const totalTitle = (row, field) => {
  if (! isTotalChanged(row, field)) return ''

  return 'was ' + figure(row['old_' + field])
}

const isPeriodChanged = (row, kind, index) => {
  if (row.status !== 'updated') return false

  return differs(row[periodField[kind]][index], row[oldPeriodField[kind]][index])
}

const periodTitle = (row, kind, index) => {
  if (! isPeriodChanged(row, kind, index)) return ''

  return 'was ' + figure(row[oldPeriodField[kind]][index])
}

const isBusy = (row) => props.busyHeads.includes(row.project_account_id)

const isEdited = (row) => props.editedHeads.includes(row.project_account_id)

const isEditable = (row) => row.project_account_id > 0 && ! row.errors.some(error => error.column === headColumn)

const canEdit = (row, field) => isEditable(row) && props.canEditField(row, field)

const isTyped = (field) => props.review.layout.typed.includes(field)

const bandClass = (index) => ({ 'period-band': index % 2 === 1 })

const listText = (names, word) => {
  if (names.length < 2) return names.join('')

  return names.slice(0, -1).join(', ') + ` ${word} ` + names[names.length - 1]
}

const splitNames = computed(() => [
  { name: 'period', count: props.review.periods.length },
  { name: 'team member', count: props.review.members.length }
].filter(item => item.count > 0).map(item => item.name))

const correctableNames = computed(() => {
  const names = props.review.layout.typed.map(field => columnName[field])

  return listText([...names, ...splitNames.value.map(name => name + ' value')], 'or')
})

const typedNames = (word) => listText(props.review.layout.typed.map(field => columnName[field]), word)

const edit = (row, field, value) => {
  emit('onEditCell', { row, field, value })
}

const editSplit = (row, field, index, value) => {
  emit('onEditCell', { row, field, index, value })
}

watch([filter, search], () => {
  page.value = 1
})

watch(() => props.review.summary.error, (count) => {
  if (count > 0 || filter.value !== 'error') return

  filter.value = 'changes'
})
</script>

<style scoped>
.review-root {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}

.hs-summary {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem 2rem;
  margin-bottom: 1rem;
  padding: .9rem 1.25rem;
  border: 1px solid #eef1f6;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 4px 18px rgba(34, 41, 47, .05);
}

.hs-stats {
  display: flex;
  flex-wrap: wrap;
  gap: .5rem 1.75rem;
}

.hs-stat {
  display: inline-flex;
  align-items: baseline;
  gap: .45rem;
}

.hs-stat-dot {
  align-self: center;
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.hs-stat-value {
  font-size: 1.4rem;
  font-weight: 700;
  color: #212529;
}

.hs-stat-name {
  font-size: .88rem;
  color: #4b5563;
}

.tone-plain {
  background: #b9b9c3;
}

.tone-success {
  background: #28c76f;
}

.tone-primary {
  background: #7367f0;
}

.tone-danger {
  background: #ea5455;
}

.tone-warning {
  background: #ff9f43;
}

.hs-total {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: .15rem;
}

.hs-total-label {
  font-size: .8rem;
  font-weight: 600;
  color: #4b5563;
  text-transform: uppercase;
  letter-spacing: .3px;
}

.hs-total-values {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: .5rem;
}

.hs-total-old {
  color: #9aa2b1;
}

.hs-total-arrow {
  font-size: .75rem;
  color: #b9b9c3;
}

.hs-total-new {
  font-size: 1.25rem;
  color: #212529;
}

.hs-diff {
  padding: .15rem .55rem;
  border-radius: 999px;
  font-size: .86rem;
  font-weight: 600;
}

.hs-diff-none {
  background: #f3f4f8;
  color: #4b5563;
}

.hs-diff-up {
  background: #e5f8ed;
  color: #28c76f;
}

.hs-diff-down {
  background: #fdecec;
  color: #ea5455;
}

.hs-alert {
  display: flex;
  align-items: flex-start;
  gap: .6rem;
  margin-bottom: .75rem;
  padding: .7rem 1rem;
  border-radius: 10px;
  font-size: .92rem;
}

.hs-alert i {
  margin-top: .2rem;
}

.hs-alert-danger {
  border: 1px solid #f8c9c9;
  background: #fdf1f1;
  color: #c43d3e;
}

.hs-alert-warning {
  border: 1px solid #ffe1c2;
  background: #fff7ef;
  color: #b8691a;
}

.hs-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: .75rem;
  margin-bottom: .75rem;
}

.hs-segment {
  display: inline-flex;
  flex-wrap: wrap;
  gap: .25rem;
  padding: .25rem;
  border: 1px solid #eef1f6;
  border-radius: 999px;
  background: #fff;
}

.hs-segment-item {
  display: inline-flex;
  align-items: center;
  gap: .4rem;
  padding: .3rem .85rem;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: #4b5563;
  font-size: .92rem;
  font-weight: 500;
  transition: background-color .15s ease, color .15s ease;
}

.hs-segment-item:hover {
  background: #f3f2fe;
  color: #7367f0;
}

.hs-segment-count {
  min-width: 20px;
  padding: 0 .35rem;
  border-radius: 999px;
  background: #f3f4f8;
  font-size: .78rem;
  line-height: 18px;
  text-align: center;
}

.hs-segment-active,
.hs-segment-active:hover {
  background: #7367f0;
  color: #fff;
  box-shadow: 0 4px 12px rgba(115, 103, 240, .3);
}

.hs-segment-active .hs-segment-count {
  background: rgba(255, 255, 255, .25);
}

.hs-edit-chip {
  display: inline-flex;
  align-items: center;
  gap: .4rem;
  padding: .3rem .75rem;
  border-radius: 999px;
  background: #fff9db;
  color: #8a6d00;
  font-size: .88rem;
}

.hs-search {
  position: relative;
  width: 260px;
  max-width: 100%;
  margin-left: auto;
}

.hs-search-icon {
  position: absolute;
  top: 50%;
  left: .75rem;
  transform: translateY(-50%);
  color: #9aa2b1;
  font-size: .86rem;
}

.hs-search .form-control {
  padding-left: 2rem;
  border-radius: 999px;
}

.table-wrap {
  flex: 0 1 auto;
  min-height: 240px;
  overflow: auto;
  border: 1px solid #eef1f6;
  border-radius: 12px;
  background: #fff;
}

.review-table {
  border-collapse: separate;
  border-spacing: 0;
  white-space: nowrap;
}

.review-table th,
.review-table td {
  padding: .5rem .75rem;
  border-right: 1px solid #f0f2f6;
  border-bottom: 1px solid #f0f2f6;
  vertical-align: top;
}

.review-table tbody td {
  padding: .6rem .8rem;
  color: #212529;
  font-size: 1.07rem;
  font-variant-numeric: tabular-nums;
}

.review-table thead th {
  position: sticky;
  z-index: 2;
  height: 36px;
  background: #f7f8fb;
  color: #343a40;
  font-size: .8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: .3px;
  vertical-align: middle;
}

.head-row-one th {
  top: 0;
}

.head-row-two th {
  top: 36px;
}

.review-table thead .period-name {
  background: #eeedfd;
  color: #4b3fd8;
  font-size: .86rem;
}

.review-table .period-start {
  border-left: 2px solid #e3e1fb;
}

.period-band {
  background: #fbfbff;
}

.review-table thead .member-name {
  max-width: 12rem;
  overflow: hidden;
  text-overflow: ellipsis;
}

.review-table .col-head {
  position: sticky;
  left: 0;
  z-index: 1;
  width: 340px;
  min-width: 340px;
  max-width: 340px;
  padding-left: 1rem;
  background: #fff;
  white-space: normal;
  box-shadow: 6px 0 10px -8px rgba(34, 41, 47, .25);
}

.review-table thead .col-head {
  z-index: 3;
  background: #f7f8fb;
}

.review-table tbody tr:hover td {
  background: #f6f6ff;
}

.review-table tbody tr:hover .cell-locked {
  background: #f3f4fa;
}

.hs-head-line {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: .5rem;
}

.hs-head-name {
  font-weight: 600;
  color: #212529;
}

.hs-head-tags {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  gap: .4rem;
}

.hs-tag {
  display: inline-flex;
  align-items: center;
  gap: .3rem;
  padding: .1rem .5rem;
  border-radius: 999px;
  font-size: .78rem;
  font-weight: 600;
}

.hs-tag-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

.hs-tag-new {
  background: #e5f8ed;
  color: #28c76f;
}

.hs-tag-updated {
  background: #eeedfd;
  color: #7367f0;
}

.hs-tag-error {
  background: #fdecec;
  color: #ea5455;
}

.hs-tag-edited {
  background: #fff3e8;
  color: #ff9f43;
}

.hs-head-meta {
  margin-top: .1rem;
  font-size: .86rem;
  color: #4b5563;
}

.hs-row-error {
  margin-top: .25rem;
  font-size: .86rem;
  color: #ea5455;
}

.hs-row-note {
  margin-top: .25rem;
  font-size: .86rem;
  color: #ff9f43;
}

.hs-undo {
  margin-top: .3rem;
  padding: 0;
  border: 0;
  background: none;
  color: #7367f0;
  font-size: .86rem;
  font-weight: 500;
}

.hs-undo:hover {
  text-decoration: underline;
}

.review-table tbody tr.row-error td,
.row-error .col-head,
.review-table tbody tr.row-error:hover td {
  background: #fdf1f1;
}

.row-new .col-head {
  box-shadow: inset 4px 0 0 #28c76f, 6px 0 10px -8px rgba(34, 41, 47, .25);
}

.row-updated .col-head {
  box-shadow: inset 4px 0 0 #7367f0, 6px 0 10px -8px rgba(34, 41, 47, .25);
}

.cell-locked {
  background: #fafbfd;
  cursor: not-allowed;
}

.review-table tbody .cell-auto {
  background: #eef5ff;
  cursor: default;
}

.review-table tbody tr:hover .cell-auto {
  background: #e6effd;
}

.review-table tbody .cell-changed,
.review-table tbody tr:hover .cell-changed {
  background: #e5f8ed;
  color: #212529;
  font-weight: 600;
}

.row-busy td:not(.col-head) {
  opacity: .55;
}

.edit-mark {
  margin-left: .35rem;
  font-size: .62rem;
  color: #7367f0;
}

.was-value {
  font-size: .76rem;
  font-weight: 400;
  color: #9aa2b1;
  text-decoration: line-through;
}

.source-name {
  font-size: .76rem;
  font-weight: 400;
  color: #9aa2b1;
}

.hs-empty {
  position: sticky;
  left: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: .5rem;
  padding: 2.5rem 1rem;
  color: #9aa2b1;
}

.hs-empty i {
  font-size: 1.5rem;
}

.hs-pager {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: .75rem;
  margin-top: .75rem;
}

.hs-pager-range,
.hs-pager-text {
  font-size: .88rem;
  color: #4b5563;
}

.hs-pager-controls {
  display: inline-flex;
  align-items: center;
  gap: .6rem;
}

.hs-page-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #e6ebf5;
  border-radius: 8px;
  background: #fff;
  color: #4b5563;
  font-size: .75rem;
}

.hs-page-btn:not(:disabled):hover {
  border-color: #7367f0;
  color: #7367f0;
}

.hs-page-btn:disabled {
  opacity: .45;
  cursor: not-allowed;
}

@media (max-width: 767.98px) {
  .hs-total {
    align-items: flex-start;
  }

  .hs-search {
    width: 100%;
    margin-left: 0;
  }

  .review-table .col-head {
    width: 220px;
    min-width: 220px;
    max-width: 220px;
  }
}
</style>
